'use client';

import { useEffect, useState } from 'react';
import { cn } from '../../../../libs/utils/TailwindMerge';
import { INotification } from './INotification';
import NotificationIcon from './NotificationIcon';

interface INotificationProps {
    notification: INotification;
    onHide: () => void;
}

const NotificationItem: React.FC<INotificationProps> = ({ notification, onHide }) => {
    const { title: name, description, icon, color, time } = notification;
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        setVisible(true);
        const timer = setTimeout(() => {
            setVisible(false);
            onHide();
        }, 3000);

        return () => clearTimeout(timer);
    }, [notification]);

    if (!visible) return null;

    return (
        <figure
            className={cn(
                'relative mx-auto min-h-fit w-full max-w-[400px] min-w-[// 300px] cursor-pointer overflow-hidden rounded-2xl p-4',
                // animation styles
                'transition-all duration-200 ease-in-out hover:scale-[103%]',
                // light styles
                'bg-white [box-shadow:0_0_0_1px_rgba(0,0,0,.03),0_2px_4px_rgba(0,0,0,.05),0_12px_24px_rgba(0,0,0,.05)]',
                // dark styles
                'dark:bg-slate-700/80 dark:[border:1px_solid_rgba(255,255,255,.1)] dark:[box-shadow:0_-20px_80px_-20px_#ffffff1f_inset]',
            )}>
            <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => {
                    setVisible(false);
                    onHide();
                }}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
            </button>
            <div className="flex flex-row items-center gap-3">
                <div className={cn('flex size-10 items-center justify-center rounded-full', color)}>
                    <div className="size-6 text-white">{<NotificationIcon iconName={icon} />}</div>
                </div>
                <div className="flex flex-col overflow-hidden">
                    <figcaption className="flex flex-row items-center whitespace-pre text-lg font-medium dark:text-white ">
                        <span className="text-sm sm:text-lg">{name}</span>
                        {time && (
                            <>
                                <span className="mx-1">·</span>
                                <span className="text-xs text-gray-500">{time}</span>
                            </>
                        )}
                    </figcaption>
                    <p className="text-sm font-normal dark:text-white/60 mr-5">
                        {<NotificationIcon description={description} />}
                    </p>
                </div>
            </div>
        </figure>
    );
};

export default NotificationItem;
